// src/components/profile/SectionRisks.jsx
import React, { useState, useMemo } from 'react';
import { 
  Shield, AlertTriangle, AlertCircle, CheckCircle, Users, FileText, 
  ChevronDown, ChevronUp, Target, Download, Filter, TrendingUp,
  Clock, ExternalLink, Copy
} from 'lucide-react';

const SeverityBadge = ({ level }) => {
  const configs = {
    'Critical': { bg: 'bg-red-600', text: 'text-white', icon: AlertCircle },
    'High': { bg: 'bg-red-500', text: 'text-white', icon: AlertTriangle },
    'Medium': { bg: 'bg-yellow-500', text: 'text-white', icon: AlertTriangle },
    'Low': { bg: 'bg-green-500', text: 'text-white', icon: CheckCircle }
  };
  
  const config = configs[level] || configs['Medium'];
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {level}
    </div>
  );
};

const LikelihoodBadge = ({ level }) => {
  const configs = {
    'Very High': { bg: 'bg-red-600', text: 'text-white' },
    'High': { bg: 'bg-orange-500', text: 'text-white' },
    'Medium': { bg: 'bg-yellow-500', text: 'text-white' },
    'Low': { bg: 'bg-blue-500', text: 'text-white' },
    'Very Low': { bg: 'bg-green-500', text: 'text-white' }
  };
  
  const config = configs[level] || configs['Medium'];
  
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {level}
    </div>
  );
};

const EnhancedRiskCard = ({ risk, index }) => {
  const getSeverityConfig = (severity) => {
    const configs = {
      'Critical': { bg: 'bg-red-50', border: 'border-red-200', accent: 'bg-red-600' },
      'High': { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'bg-orange-600' },
      'Medium': { bg: 'bg-yellow-50', border: 'border-yellow-200', accent: 'bg-yellow-600' },
      'Low': { bg: 'bg-green-50', border: 'border-green-200', accent: 'bg-green-600' }
    };
    return configs[severity] || configs['Medium'];
  };

  const config = getSeverityConfig(risk.severity);

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 ${config.accent} text-white rounded-full flex items-center justify-center text-sm font-bold`}>
          {index + 1}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-gray-900">{risk.name}</h4>
            <SeverityBadge level={risk.severity} />
            <LikelihoodBadge level={risk.likelihood} />
            <div className="text-sm font-bold text-blue-600">
              Score: {risk.score.toFixed(1)}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Owner:</span>
              <div className="text-sm text-gray-800 mt-1">{risk.owner || 'Unassigned'}</div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Category:</span>
              <div className="mt-1">
                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {risk.category}
                </span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Mitigation Strategy:</span>
              <p className="text-sm text-gray-700 mt-1">{risk.mitigation || 'No mitigation strategy defined'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RiskScore = ({ severity, likelihood }) => {
  const severityMap = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
  const likelihoodMap = { 'Very High': 4, 'High': 3, 'Medium': 2, 'Low': 1, 'Very Low': 0.5 };
  
  const score = (severityMap[severity] || 2) * (likelihoodMap[likelihood] || 2);
  
  const getScoreColor = (score) => {
    if (score >= 12) return 'text-red-600 font-bold';
    if (score >= 8) return 'text-orange-600 font-semibold';
    if (score >= 4) return 'text-yellow-600 font-medium';
    return 'text-green-600';
  };
  
  return (
    <div className={`text-sm ${getScoreColor(score)}`}>
      {score.toFixed(1)}
    </div>
  );
};

export default function SectionRisks({ 
  profileData, 
  risks = [],
  isCustomized = false,
  clientId,
  useCaseId
}) {

  // State declarations
  const [showAllRisks, setShowAllRisks] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  
  // Get implementation readiness/complexity from profileData
  const readiness = profileData?.implementation?.readiness || 'Medium';
  const complexity = profileData?.implementation?.complexity || 'Medium';
  
  // Risk scoring maps
  const severityMap = useMemo(() => ({ 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 }), []);
  const likelihoodMap = useMemo(() => ({ 'Very High': 4, 'High': 3, 'Medium': 2, 'Low': 1, 'Very Low': 0.5 }), []);
  
  // Process risks with scoring
  const processedRisks = useMemo(() => {
    const riskArray = Array.isArray(risks) ? risks : risks?.data || [];
    
    return riskArray.map(risk => ({
      ...risk,
      score: (severityMap[risk.severity] || 2) * (likelihoodMap[risk.likelihood] || 2)
    }));
  }, [risks, severityMap, likelihoodMap]);
  
  // Filter and sort risks
  const filteredRisks = useMemo(() => {
    let filtered = processedRisks;
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(risk => risk.category === filterCategory);
    }
    
    // Sort by score (default) or other criteria
    filtered.sort((a, b) => {
      if (sortBy === 'score') return b.score - a.score;
      if (sortBy === 'severity') return severityMap[b.severity] - severityMap[a.severity];
      if (sortBy === 'likelihood') return likelihoodMap[b.likelihood] - likelihoodMap[a.likelihood];
      return 0;
    });
    
    return filtered;
  }, [processedRisks, filterCategory, sortBy, severityMap, likelihoodMap]);

  // Top 3 critical risks
  const topRisks = filteredRisks.slice(0, 3);
  
  // Calculate heat score
  const heatScore = useMemo(() => {
    if (processedRisks.length === 0) return '0.0';

    // Calculate average risk score across all risks
    const totalScore = processedRisks.reduce((sum, r) => sum + r.score, 0);
    const avgScore = totalScore / processedRisks.length;

    // Max possible score is 16 (Critical=4 * Very High=4)
    const normalizedScore = (avgScore / 16) * 100;

    return normalizedScore.toFixed(0);
  }, [processedRisks]);
  
  // Get unique categories
  const categories = [...new Set(processedRisks.map(r => r.category))];
  
  // Get readiness/complexity colors
  const getReadinessColor = (level) => {
    switch(level) {
      case 'High': return 'bg-green-100 text-green-800';
      case 'Low': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };
  
  const getComplexityColor = (level) => {
    switch(level) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'High': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Export to CSV function
  const exportToCSV = () => {
    const csvData = filteredRisks.map(r => 
      `${r.name},${r.category},${r.severity},${r.likelihood},${r.score.toFixed(1)},${r.owner || ''},"${r.mitigation || ''}"`
    ).join('\n');
    const blob = new Blob([`Risk,Category,Severity,Likelihood,Score,Owner,Mitigation\n${csvData}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risk-register-uc${useCaseId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show empty state if no risks
  if (processedRisks.length === 0) {
    return (
      <section className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No risks have been identified for this use case.</p>
          <p className="text-sm text-gray-500 mt-2">Risk assessment will be completed during implementation planning.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="risks-governance" aria-labelledby="risks-title" className="scroll-mt-24">
      {/* Risk Snapshot Strip */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-orange-200 shadow-sm -mx-6 px-6 py-3 mb-6">
        <div className="flex items-center justify-between">
          {/* Left: Risk Metrics */}
          <div className="flex items-center gap-6">
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">High Priority Risks</div>
              <div className="text-3xl font-bold text-orange-600">
                {processedRisks.filter(r => r.severity === 'Critical' || r.severity === 'High').length}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Risk Heat Index</div>
              <div className="text-2xl font-bold text-red-600">{heatScore}%</div>
            </div>
          </div>
          
          {/* Center: Feasibility Chips */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Readiness</div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getReadinessColor(readiness)}`}>
                {readiness}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Complexity</div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getComplexityColor(complexity)}`}>
                {complexity}
              </div>
            </div>
          </div>
          
          {/* Right: Actions & Source Indicator */}
          <div className="flex items-center gap-3">
            {isCustomized && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                ✨ Customized
              </span>
            )}
            <button 
              onClick={exportToCSV}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Top 3 Critical Risks Cards */}
      {topRisks.length > 0 && (
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Top Risk Priorities
            </h3>
            <div className="space-y-4">
              {topRisks.map((risk, idx) => (
                <EnhancedRiskCard key={idx} risk={risk} index={idx} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Complete Risk Register with Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#18365E] flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Complete Risk Register
          </h3>
          <div className="flex items-center gap-3">
            {/* Filters */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select 
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="score">Sort by Score</option>
                <option value="severity">Sort by Severity</option>
                <option value="likelihood">Sort by Likelihood</option>
              </select>
            </div>
            <button
              onClick={() => setShowAllRisks(!showAllRisks)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#FF6E4C] hover:text-[#e55a3c] border border-[#FF6E4C] hover:border-[#e55a3c] rounded-lg transition-colors"
            >
              {showAllRisks ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showAllRisks ? 'Hide Register' : `Show All ${filteredRisks.length} Risks`}
            </button>
          </div>
        </div>
        
        {showAllRisks && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-gray-600">
                  <th className="px-4 py-3 text-left font-medium">Risk</th>
                  <th className="px-4 py-3 text-left font-medium">Severity</th>
                  <th className="px-4 py-3 text-left font-medium">Likelihood</th>
                  <th className="px-4 py-3 text-left font-medium">Score</th>
                  <th className="px-4 py-3 text-left font-medium">Owner</th>
                  <th className="px-4 py-3 text-left font-medium">Mitigation</th>
                </tr>
              </thead>
              <tbody>
                {filteredRisks.map((risk, idx) => (
                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{risk.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {risk.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <SeverityBadge level={risk.severity} />
                    </td>
                    <td className="px-4 py-3">
                      <LikelihoodBadge level={risk.likelihood} />
                    </td>
                    <td className="px-4 py-3">
                      <RiskScore severity={risk.severity} likelihood={risk.likelihood} />
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{risk.owner || 'Unassigned'}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">
                      <div className="text-xs truncate" title={risk.mitigation}>
                        {risk.mitigation || 'No mitigation defined'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {!showAllRisks && (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Click "Show All Risks" to view the complete risk register</p>
            <p className="text-xs text-gray-400 mt-2">
              {filteredRisks.length} risks • Filtered by: {filterCategory === 'all' ? 'All Categories' : filterCategory}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}